import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Copy, ExternalLink, Loader2, RefreshCw, Globe } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { demoUrl, errMsg, formatDate, useApi } from "@/lib/api";
import TempDemoLinks from "./TempDemoLinks";

export default function ShareDialog({ project, open, onOpenChange, onChange, beforePublish, afterPublish, warning }) {
  const api = useApi();
  const share = project?.share || {};
  const [slug, setSlug] = useState(share.slug || "");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setSlug(share.slug || ""); }, [share.slug, open]);
  if (!project) return null;
  const url = demoUrl(share.slug);
  const unpublished = share.published_at && project.updated_at && new Date(project.updated_at) > new Date(share.published_at);

  const update = async (enabled, newSlug = share.slug) => {
    setBusy(true);
    try {
      if (enabled && beforePublish) await beforePublish();
      const { data } = await api.put(`/projects/${project.id}/share`, { enabled, slug: newSlug });
      onChange?.({ share: data });
      if (enabled) await afterPublish?.();
      toast.success(enabled ? "Condivisione attiva" : "Condivisione disattivata");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const publish = async () => {
    setBusy(true);
    try {
      if (beforePublish) await beforePublish();
      const { data } = await api.post(`/projects/${project.id}/publish`);
      onChange?.({ share: { ...share, published_at: data.published_at } });
      await afterPublish?.();
      toast.success("Demo pubblica aggiornata");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); toast.success("Link copiato"); } catch { toast.message(url); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="share-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Globe size={18} className="text-sky-600" /> Condividi demo</DialogTitle>
          <DialogDescription>Il cliente vede solo la demo pubblicata: nessun accesso all'editor o ad altri progetti.</DialogDescription>
        </DialogHeader>
        {warning && <p className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800 flex gap-2" data-testid="share-diagnostics-warning"><AlertTriangle size={14} className="shrink-0 mt-0.5" />{warning}</p>}
        <div className="flex items-center justify-between rounded-lg border p-4">
          <div>
            <Label className="text-sm">Link pubblico attivo</Label>
            <p className="text-xs text-slate-500 mt-0.5">Disattivalo per rendere la demo irraggiungibile.</p>
          </div>
          <Switch checked={!!share.enabled} disabled={busy} onCheckedChange={(v) => update(v)} data-testid="share-enabled-switch" />
        </div>
        <div className="space-y-2">
          <Label className="text-xs text-slate-500">Indirizzo della demo</Label>
          <div className="flex gap-2">
            <div className="flex-1 flex items-center rounded-md border bg-slate-50 pl-3 text-xs text-slate-500 overflow-hidden">
              <span className="shrink-0">/demo/</span>
              <Input value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
                className="border-0 bg-transparent h-9 px-1 text-sm text-slate-900 focus-visible:ring-0" data-testid="share-slug-input" />
            </div>
            <Button variant="outline" disabled={busy || slug === share.slug || !slug} onClick={() => update(!!share.enabled, slug)} data-testid="share-slug-save-btn">Salva</Button>
          </div>
        </div>
        {share.enabled && (
          <div className="space-y-3">
            <div className="flex gap-2">
              <Input readOnly value={url} className="font-mono text-xs" data-testid="share-url-input" />
              <Button onClick={copy} data-testid="share-copy-btn"><Copy size={15} className="mr-1.5" />Copia link</Button>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-slate-50 border p-3">
              <div className="text-xs text-slate-600">
                <p>Ultima pubblicazione: <span className="font-medium" data-testid="share-published-at">{formatDate(share.published_at, true)}</span></p>
                {unpublished && <p className="text-amber-700 mt-0.5" data-testid="share-unpublished-warning">Ci sono modifiche non ancora pubblicate</p>}
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" asChild><a href={url} target="_blank" rel="noreferrer" data-testid="share-open-btn"><ExternalLink size={14} className="mr-1.5" />Apri</a></Button>
                <Button size="sm" onClick={publish} disabled={busy} data-testid="share-publish-btn">
                  {busy ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <RefreshCw size={14} className="mr-1.5" />}Aggiorna demo pubblica
                </Button>
              </div>
            </div>
          </div>
        )}
        {open && project?.id && <TempDemoLinks pid={project.id} />}
      </DialogContent>
    </Dialog>
  );
}
